import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Book, Lightbulb, Target, Edit3, 
  AlertTriangle, X, Loader2
} from 'lucide-react';

/**
 * Module d'actions sur les recommandations
 * Responsabilités:
 * - Proposer les actions disponibles suite aux recommandations
 * - Gérer la navigation vers les différents espaces
 * - Permettre la modification/révision de l'objectif
 * - Gérer la suppression de l'objectif et ses recommandations
 */
const RecomActions = ({
  competenceId,
  onReviseObjective,
  onRegenerateRecommendations,
  onDeleteObjectif,
  isGeneratingPlan = false,
  isDeleting = false
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Gérer la demande de suppression
  const handleDeleteRequest = () => {
    setShowDeleteConfirm(true);
  };

  // Fermer la modal de confirmation
  const handleCloseModal = () => {
    setShowDeleteConfirm(false);
  };

  // Confirmer la suppression
  const handleConfirmDelete = () => {
    onDeleteObjectif();
  };

  return (
    <>
      {/* Actions principales */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 shadow-sm border border-gray-100 mt-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Prochaines étapes recommandées</h3>
        
        {/* Navigation vers les espaces */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to={`/actionPlan/${competenceId}`}
            className="bg-white rounded-lg p-4 flex flex-col items-center text-center shadow hover:shadow-md transition duration-200"
          >
            <Target className="w-10 h-10 mb-2 text-blue-500" />
            <div className="font-semibold">Espace Action</div>
            <div className="text-sm text-gray-600">Démarrer votre plan d'actions</div>
          </Link>

          <Link to={`/exploration/${competenceId}`}
            className="bg-white rounded-lg p-4 flex flex-col items-center text-center shadow hover:shadow-md transition duration-200"
          >
            <Book className="w-10 h-10 mb-2 text-green-500" />
            <div className="font-semibold">Espace Exploration</div>
            <div className="text-sm text-gray-600">Approfondissez les concepts clés</div>
          </Link>
          
          <Link to={`/inspiration/${competenceId}`}
            className="bg-white rounded-lg p-4 flex flex-col items-center text-center shadow hover:shadow-md transition duration-200"
          >
            <Lightbulb className="w-10 h-10 mb-2 text-yellow-500" />
            <div className="font-semibold">Espace Inspiration</div>
            <div className="text-sm text-gray-600">Explorez des idées liées à vos objectifs</div>
          </Link>

        </div>
        
        {/* Boutons d'action secondaires */}
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <button
            onClick={onReviseObjective}
            className="flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
          >
            <Edit3 className="w-4 h-4 mr-2" />
            Réviser mon objectif
          </button>
          
          <button
            onClick={onRegenerateRecommendations}
            disabled={isGeneratingPlan}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              isGeneratingPlan 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {isGeneratingPlan ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Génération en cours...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Régénérer les recommandations
              </>
            )}
          </button>
          
          <button
            onClick={handleDeleteRequest}
            className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
          >
            <X className="w-4 h-4 mr-2" />
            Supprimer cet objectif
          </button>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center text-red-600 mb-4">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <h3 className="text-xl font-bold">Supprimer cet objectif ?</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Cette action est irréversible. Toutes les données associées à cet objectif, y compris les recommandations, seront définitivement supprimées.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={isDeleting}
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4 mr-1" />
                    Supprimer définitivement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RecomActions;